/**
 * PATH: apps/ai/src/pages/CalculationsPage.tsx
 * PURPOSE: AI-powered calculations and estimates
 */

import { useState } from 'react';
import { Calculator, TrendingUp, DollarSign, Clock, Target, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

interface EstimateResult {
  id: string;
  description: string;
  estimatedCost: number;
  confidence: number;
  breakdown: Array<{
    category: string;
    amount: number;
    percentage: number;
  }>;
  similarJobs: Array<{
    name: string;
    cost: number;
    variance: number;
  }>;
}

export default function CalculationsPage() {
  const [scope, setScope] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [estimates, setEstimates] = useState<EstimateResult[]>([
    {
      id: '1',
      description: 'Commercial office fitout - 500 sqm',
      estimatedCost: 125000,
      confidence: 0.87,
      breakdown: [
        { category: 'Materials', amount: 45000, percentage: 36 },
        { category: 'Labor', amount: 55000, percentage: 44 },
        { category: 'Equipment', amount: 12500, percentage: 10 },
        { category: 'Overheads', amount: 12500, percentage: 10 },
      ],
      similarJobs: [
        { name: 'Office Fitout - Tech Company', cost: 118000, variance: -5.6 },
        { name: 'Commercial Space Renovation', cost: 132000, variance: 5.6 },
        { name: 'Corporate Office Build', cost: 128000, variance: 2.4 },
      ],
    },
  ]);

  const handleCalculate = () => {
    if (!scope.trim()) return;
    
    setIsCalculating(true);
    
    // Simulate AI calculation
    setTimeout(() => {
      const baseCost = Math.floor(Math.random() * 100000) + 50000;
      const newEstimate: EstimateResult = {
        id: Date.now().toString(),
        description: scope,
        estimatedCost: baseCost,
        confidence: 0.75 + Math.random() * 0.2,
        breakdown: [
          { category: 'Materials', amount: Math.floor(baseCost * 0.35), percentage: 35 },
          { category: 'Labor', amount: Math.floor(baseCost * 0.45), percentage: 45 },
          { category: 'Equipment', amount: Math.floor(baseCost * 0.1), percentage: 10 },
          { category: 'Overheads', amount: Math.floor(baseCost * 0.1), percentage: 10 },
        ],
        similarJobs: [
          { name: 'Similar Project A', cost: baseCost * 0.95, variance: -5 },
          { name: 'Similar Project B', cost: baseCost * 1.08, variance: 8 },
          { name: 'Similar Project C', cost: baseCost * 1.02, variance: 2 },
        ],
      };
      
      setEstimates((prev) => [newEstimate, ...prev]);
      setScope('');
      setIsCalculating(false);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Calculations</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Intelligent cost estimates based on historical data and AI analysis
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { icon: Calculator, label: 'Estimates Generated', value: '1,284', change: '+12%' },
          { icon: Target, label: 'Avg Accuracy', value: '87%', change: '+3%' },
          { icon: DollarSign, label: 'Total Estimated', value: '$4.2M', change: '+18%' },
          { icon: Clock, label: 'Avg Time Saved', value: '2.5 hrs', change: 'per estimate' },
        ].map((stat, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <stat.icon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
                <p className="text-xs text-green-600">{stat.change}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Input Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg text-white">
            <Calculator className="w-5 h-5" />
          </div>
          <h2 className="font-semibold text-gray-900 dark:text-white">Generate Cost Estimate</h2>
        </div>

        <div className="space-y-4">
          <textarea
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="Describe the project scope in detail...&#10;e.g., Commercial kitchen renovation including electrical upgrade, plumbing, ventilation, and custom cabinetry for a 100 sqm space"
            className="w-full h-32 px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
          />
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Include details like area size, materials, and specific requirements for better accuracy
            </p>
            <button
              onClick={handleCalculate}
              disabled={!scope.trim() || isCalculating}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
            >
              {isCalculating ? (
                <>
                  <Sparkles className="w-5 h-5 animate-pulse" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="w-5 h-5" />
                  Calculate Estimate
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Estimates List */}
      <div className="space-y-6">
        {estimates.map((estimate) => (
          <div
            key={estimate.id}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-amber-500">
              <div className="flex items-center justify-between text-white">
                <div>
                  <p className="text-sm opacity-80">Estimated Cost</p>
                  <p className="text-3xl font-bold">${estimate.estimatedCost.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-80">Confidence</p>
                  <p className="text-2xl font-bold">{(estimate.confidence * 100).toFixed(0)}%</p>
                </div>
              </div>
              <p className="text-white/80 mt-2">{estimate.description}</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cost Breakdown */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Cost Breakdown</h3>
                  <div className="space-y-3">
                    {estimate.breakdown.map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600 dark:text-gray-400">{item.category}</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            ${item.amount.toLocaleString()} ({item.percentage}%)
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Similar Jobs */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Similar Projects</h3>
                  <div className="space-y-3">
                    {estimate.similarJobs.map((job, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">{job.name}</p>
                          <p className="text-sm text-gray-500">${job.cost.toLocaleString()}</p>
                        </div>
                        <span
                          className={cn(
                            "text-sm font-medium",
                            job.variance >= 0 ? "text-red-600" : "text-green-600"
                          )}
                        >
                          {job.variance >= 0 ? '+' : ''}{job.variance.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <button className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                  Create Quote from Estimate
                </button>
                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  Export Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

